/**
 * The old address, telling people where the new one is — and then taking them there.
 *
 * ProTrack now answers on protrackagent.com. The old address still works, because bookmarks and
 * every link in every email ever sent still point at it, and breaking those would strand people
 * with no way to tell what had happened. So it keeps working and carries this instead.
 *
 * WHY A COUNTDOWN AND NOT AN IMMEDIATE REDIRECT. An instant redirect is the usual way to do this
 * and it is wrong here: somebody halfway through writing a deliverable note would lose it with no
 * warning and no idea why. Forty seconds is long enough to read a sentence and decide, and the
 * "Stay here" button stops the clock entirely — because the one outcome worth avoiding is
 * throwing away work somebody was in the middle of.
 *
 * WHY IT SAYS "SIGN IN AGAIN". The session lives in localStorage, and localStorage belongs to an
 * origin rather than to a company. Arriving on the new address therefore means signing in once
 * more. That surprises people into thinking something is broken, so it is said in advance.
 *
 * Derived from the hostname, like EnvironmentBanner, and for the same reason: a build flag has to
 * be set correctly, a hostname simply is. Every other address — including the new one — shows
 * nothing at all.
 */
import React, { useEffect, useState } from 'react'

const OLD_HOST = 'protrack.3pillarsdata.com'
const NEW_ORIGIN = 'https://protrackagent.com'
const SECONDS = 40
// Remembered for the tab, not the browser: "stay here" means this visit, not for ever. A new
// session gets the notice again, which is the point — the old address is going away eventually.
const STAY_KEY = 'moved.stayOnOldAddress'

export function onOldAddress() {
  if (typeof window === 'undefined') return false
  return (window.location.hostname || '').toLowerCase() === OLD_HOST
}

/** The same page on the new address — path, query and anchor kept, so a deep link stays deep. */
function newUrl() {
  const { pathname, search, hash } = window.location
  return `${NEW_ORIGIN}${pathname}${search}${hash}`
}

export default function MovedBanner() {
  const [left, setLeft] = useState(SECONDS)
  const [staying, setStaying] = useState(() => {
    try {
      return sessionStorage.getItem(STAY_KEY) === '1'
    } catch {
      return false
    }
  })

  const showing = onOldAddress() && !staying

  useEffect(() => {
    if (!showing) return undefined
    if (left <= 0) {
      window.location.replace(newUrl())
      return undefined
    }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000)
    return () => clearTimeout(t)
  }, [showing, left])

  if (!showing) return null

  const stay = () => {
    try {
      sessionStorage.setItem(STAY_KEY, '1')
    } catch {
      /* private mode: the button still works for this render, which is enough */
    }
    setStaying(true)
  }

  return (
    <div
      role="status"
      className="w-full bg-amber-50 border-b-2 border-amber-400 text-amber-900 px-4 py-2.5 text-sm"
    >
      <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
        <span>
          <b>ProTrack has a new address: protrackagent.com</b>
          {' — '}
          this page will take you there in <b>{left}s</b>.
        </span>
        <span className="text-amber-800">
          Nothing has moved or changed. It is the same ProTrack, the same information, on the same
          secure server — you will just need to sign in once on the new address.
        </span>
        <span className="flex items-center gap-2">
          <a
            href={newUrl()}
            className="inline-block bg-amber-700 hover:bg-amber-800 text-white font-semibold rounded px-3 py-1 text-xs no-underline"
          >
            Go there now
          </a>
          <button
            type="button"
            onClick={stay}
            className="inline-block bg-white hover:bg-amber-100 border border-amber-400 text-amber-900 font-semibold rounded px-3 py-1 text-xs"
          >
            Stay here for now
          </button>
        </span>
      </div>
    </div>
  )
}
